import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ChevronRight,
  Info,
  Layers,
  Loader2,
  MoreVertical,
  Sparkles,
  Trash2,
} from 'lucide-react';

import type { IpttqIskSlotKind, IpttqRecord } from '@kelilearning/psycheflow-contracts';
import {
  formatIpttqBankMetaLine,
  getIskSlotImage,
  getIskSlots,
  ipttqHasSkillBody,
  ipttqIskIsStale,
  ipttqIskPlaceholderHint,
  ipttqIskWarningLabel,
  ipttqNeedsBankWarning,
  ipttqNeedsIskWarning,
  iskSlotHasImage,
  isIpttqSessionOriginalsRef,
  resolveIskSelectedSlot,
} from '@kelilearning/psycheflow-contracts';

import { DocumentMathTextBlock } from '../components/MathTextBlock';
import { ProxyImage } from '../components/assets/ProxyImage';
import {
  AiGeneratedImageCornerMark,
  AiGeneratedInlineMark,
  AI_GENERATED_MARK_ON_IPTTQ_IMPLICIT_ENABLED,
} from '../components/AiGeneratedContentBadge';
import { IskErrorNotice } from '../components/ipttq/IskErrorNotice';
import { ipttqService, type IpttqRefreshScope } from '../services/ipttqService';
import type { KnowledgeParticle } from '../types/domain';
import { buildKnowledgeParticleDetailPath } from '../utils/knowledgeNavigation';
import { getParticlePreviewText, getParticleTitle } from '../utils/particleDisplay';
import { fetchWorkbenchParticles } from '../features/ttqib/ttqWorkbenchParticleCache';
import { peekIpttqRecord } from '../services/ipttqRecordCache';
import { scrubIpttqLearnerFacingNotes, stripIpttqDomainTerms } from '../features/ttqib/ipttqLearnerFacingText';
import { formatIskCostDisplay } from '../utils/iskCostLog';
import {
  canUseIskAiGeneration,
  ISK_AI_GENERATION_RESTRICTED_NOTICE,
} from '../utils/iskAiGenerationGate';
import { useAuth } from '../context/AuthContext';
import { ttqService } from '../services/ttqService';
import { IPTTQ_SCOPE_DRIFT_TIP, isIpttqScopeDrifted, sessionParticleIdKey } from '../features/ttqib/ipttqScopeDrift';

/** ISK：WaveSpeed（GPT Image 2 / Seedream / Nano Banana Pro）+ Qwen Image 3.0 Pro */
const ISK_IMAGE_MODEL_OPTIONS: Array<{
  id: string;
  label: string;
  provider: string;
  unavailable?: boolean;
}> = [
  { id: 'gpt-image-2', label: 'OpenAI GPT Image 2（WaveSpeed）', provider: 'image2' },
  { id: 'seedream-v5.0-pro', label: 'Seedream 5.0 Pro（更便宜）', provider: 'wavespeed' },
  { id: 'nano-banana-pro', label: 'Nano Banana Pro（WaveSpeed）', provider: 'banana' },
  {
    id: 'qwen-image-3.0-pro',
    label: 'Qwen Image 3.0 Pro',
    provider: 'dashscope',
  },
];

function resolveIskModelOptions(
  remoteProviders?: Array<{
    id?: string;
    models?: Array<{ id: string; label: string; provider?: string; unavailable?: boolean }>;
  }> | null,
): Array<{ id: string; label: string; provider: string; unavailable?: boolean }> {
  const byId = new Map(
    ISK_IMAGE_MODEL_OPTIONS.map((item) => [item.id, { ...item }] as const),
  );
  for (const provider of remoteProviders || []) {
    for (const model of provider.models || []) {
      const id = String(model.id || '').trim();
      if (!id) continue;
      const prev = byId.get(id);
      if (!prev) {
        byId.set(id, {
          id,
          label: String(model.label || id).trim(),
          provider: String(model.provider || provider.id || 'wavespeed').trim(),
          unavailable: Boolean(model.unavailable),
        });
        continue;
      }
      byId.set(id, {
        id,
        label: String(model.label || '').trim() || prev.label,
        provider: String(model.provider || provider.id || prev.provider).trim() || prev.provider,
        // 已上线模型：远端旧 catalog 的 unavailable 不再覆盖本地开放态
        unavailable: id === 'qwen-image-3.0-pro' || id === 'gpt-image-2'
          ? false
          : (model.unavailable != null ? Boolean(model.unavailable) : prev.unavailable),
      });
    }
  }
  // 保持本地目录顺序，再追加远端新增项
  const ordered = ISK_IMAGE_MODEL_OPTIONS.map((item) => byId.get(item.id)!);
  for (const [id, item] of byId) {
    if (!ISK_IMAGE_MODEL_OPTIONS.some((local) => local.id === id)) ordered.push(item);
  }
  return ordered;
}

function ChipList({ items, empty }: { items?: string[]; empty: string }) {
  if (!items?.length) {
    return <p className="text-xs text-muted">{empty}</p>;
  }
  return (
    <ul className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <li
          key={item}
          className="rounded-md border border-border bg-canvas/60 px-2 py-0.5 text-[11px] text-ink"
        >
          <DocumentMathTextBlock text={item} inline className="text-[11px]" />
        </li>
      ))}
    </ul>
  );
}

function ImplicitCapabilityList({
  capabilities,
  bindings,
}: {
  capabilities?: string[];
  bindings?: IpttqRecord['skill']['implicitCapabilityBindings'];
}) {
  if (!capabilities?.length) {
    return <p className="text-xs text-muted">（尚未标注）</p>;
  }
  const bindingByText = new Map(
    (bindings || []).map((item) => [String(item.text || '').trim(), item] as const),
  );
  return (
    <ul className="space-y-2">
      {capabilities.map((text, index) => {
        const binding = bindingByText.get(text) || bindings?.[index] || null;
        const mindSetLabel = binding?.implicitAbilityTagId
          ? [binding.category, binding.label].filter(Boolean).join(' · ')
          : '';
        return (
          <li
            key={`${text}-${index}`}
            className="rounded-lg border border-border bg-canvas/40 px-3 py-2"
          >
            <div className="ai-chat-assistant-body">
              <DocumentMathTextBlock text={text} className="text-sm text-ink" />
              {AI_GENERATED_MARK_ON_IPTTQ_IMPLICIT_ENABLED ? (
                <AiGeneratedInlineMark baseFontSize={14} />
              ) : null}
            </div>
            {mindSetLabel ? (
              <p className="mt-1 text-[11px] font-medium text-primary">
                MindSet · {mindSetLabel}
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-muted">
                未匹配到 MindSet 词表（保留原文）
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** 空壳草稿 / 占位思考脉络（待根据、由模型建议创建…）→ 浓缩未就绪 */
function skillLooksEmpty(record: IpttqRecord): boolean {
  return !ipttqHasSkillBody(record.skill);
}

/** 备注只展示与品题浓缩结果内容相关的说明，过滤产品操作与年龄元说明 */
function resolveLearnerFacingNotes(value: unknown): string | null {
  return scrubIpttqLearnerFacingNotes(value);
}

function iskGenerationPending(sketch: IpttqRecord['skill']['intuitionSketch']): boolean {
  if (!sketch) return false;
  if (sketch.status === 'pending') return true;
  const slots = getIskSlots(sketch);
  return slots.original?.status === 'pending' || slots.repaired?.status === 'pending';
}

const ISK_SLOT_ROWS: Array<{ kind: IpttqIskSlotKind; label: string }> = [
  { kind: 'original', label: '原始AI生成直觉草图' },
  { kind: 'handDrawn', label: '手绘直觉草图' },
  { kind: 'repaired', label: '手绘直觉草图AI修图' },
];

type BankRunState = 'idle' | 'running';

/** 品题浓缩结果详情页：返回品题详情；诱导题进入知识颗粒后回到本页并高亮对应题卡 */
export function TtqIpttqDetailPage() {
  const { ttqaId = '', ipttqId = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const iskAiGenerationAllowed = canUseIskAiGeneration(user?.phone);
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionPath = `/ttq/${encodeURIComponent(ttqaId)}`;
  const ipttqPath = `/ttq/${encodeURIComponent(ttqaId)}/ipttq/${encodeURIComponent(ipttqId)}`;
  const focusParticleId = searchParams.get('focusParticle')?.trim() || '';

  const [record, setRecord] = useState<IpttqRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refreshingScope, setRefreshingScope] = useState<IpttqRefreshScope | null>(null);
  const [bankRunState, setBankRunState] = useState<BankRunState>('idle');
  const [bankCount, setBankCount] = useState(1);
  const [bankParticles, setBankParticles] = useState<Record<string, KnowledgeParticle | null>>({});
  const [bankLoading, setBankLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [refreshingIsk, setRefreshingIsk] = useState(false);
  const [uploadingIsk, setUploadingIsk] = useState(false);
  const [repairingIsk, setRepairingIsk] = useState(false);
  const [iskPreviewSlot, setIskPreviewSlot] = useState<IpttqIskSlotKind | null>(null);
  const [iskModels, setIskModels] = useState(ISK_IMAGE_MODEL_OPTIONS);
  const [iskDefaultModel, setIskDefaultModel] = useState('gpt-image-2');
  const [iskModel, setIskModel] = useState('gpt-image-2');
  const [iskProvider, setIskProvider] = useState('image2');
  const [iskCostRecordHint, setIskCostRecordHint] = useState<string | null>(null);
  const [iskCostVisible, setIskCostVisible] = useState(false);
  const [sessionTokenHintLoading, setSessionTokenHintLoading] = useState(false);
  const [sessionParticleCount, setSessionParticleCount] = useState<number | null>(null);
  const [sessionParticleKey, setSessionParticleKey] = useState<string | null>(null);
  const iskUploadInputRef = useRef<HTMLInputElement | null>(null);
  const menuRootRef = useRef<HTMLDivElement | null>(null);

  const startingBankRef = useRef(false);
  const noReferenceParticles = sessionParticleCount === 0
    || (sessionParticleCount == null && (record?.referenceParticleIds?.length ?? 0) === 0);
  const scopeDrifted = Boolean(record && sessionParticleKey != null)
    && isIpttqScopeDrifted(record, sessionParticleKey || '');

  const applyBankGenerationState = (next: IpttqRecord | null) => {
    const gen = next?.bankGeneration;
    if (gen?.status === 'running') {
      setBankRunState('running');
      return;
    }
    setBankRunState('idle');
    if (gen?.status === 'failed' && gen.errorMessage) {
      setError(gen.errorMessage);
      setNotice(null);
    } else if (gen?.status === 'completed') {
      setNotice(null);
      if (gen.errorMessage) setError(gen.errorMessage);
    }
  };

  const remainingSlots = record ? Math.max(0, 5 - record.bankParticleIds.length) : 0;
  const maxCount = useMemo(() => Math.max(1, remainingSlots || 1), [remainingSlots]);
  const clampedCount = Math.min(Math.max(1, bankCount), maxCount);

  useEffect(() => {
    let cancelled = false;
    void ipttqService.listIskImageGenerationProviders()
      .then((data) => {
        if (cancelled) return;
        const models = resolveIskModelOptions(data.providers);
        setIskModels(models);
        const preferred = models.find((item) => item.id === 'gpt-image-2' && !item.unavailable)
          || models.find((item) => item.id === data.defaultModel && !item.unavailable)
          || models.find((item) => !item.unavailable)
          || models[0]
          || ISK_IMAGE_MODEL_OPTIONS[0];
        setIskDefaultModel(preferred.id);
        setIskModel(preferred.id);
        setIskProvider(preferred.provider || 'image2');
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const root = menuRootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!ttqaId) return;
    let cancelled = false;
    void ttqService.getSession(ttqaId)
      .then((session) => {
        if (cancelled) return;
        setSessionParticleCount(session.particles?.length ?? 0);
        setSessionParticleKey(sessionParticleIdKey(session));
      })
      .catch(() => {
        if (!cancelled) {
          setSessionParticleCount(null);
          setSessionParticleKey(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ttqaId]);

  useEffect(() => {
    if (!ipttqId) return;
    let cancelled = false;
    const cached = peekIpttqRecord(ipttqId);
    if (cached) {
      setRecord(cached);
      applyBankGenerationState(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    setNotice(null);
    // 进入详情始终拉最新（题库后台生成后离开再进可看到已生成题目）
    void ipttqService.get(ipttqId, { forceRefresh: true })
      .then((data) => {
        if (cancelled) return;
        setRecord(data);
        applyBankGenerationState(data);
      })
      .catch((err) => {
        if (!cancelled && !cached) {
          setRecord(null);
          setError(err instanceof Error ? err.message : '加载品题浓缩结果失败');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ipttqId]);

  useEffect(() => {
    if (!record) return;
    setBankCount(Math.min(5, Math.max(1, Math.max(0, 5 - record.bankParticleIds.length) || 1)));
  }, [record?.id, record?.bankParticleIds.length]);

  /** ISK pending：首次与回前台拉取 */
  useEffect(() => {
    const pending = iskGenerationPending(record?.skill?.intuitionSketch);
    if (!ipttqId || !pending) {
      if (record && !iskGenerationPending(record.skill?.intuitionSketch)) {
        setRefreshingIsk(false);
        setRepairingIsk(false);
      }
      return;
    }
    setRefreshingIsk(true);
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await ipttqService.get(ipttqId, { forceRefresh: true });
        if (cancelled) return;
        setRecord(data);
        applyBankGenerationState(data);
        const sketch = data.skill?.intuitionSketch;
        if (!iskGenerationPending(sketch)) {
          setRefreshingIsk(false);
          setRepairingIsk(false);
          const slots = getIskSlots(sketch);
          const failed = slots.original?.status === 'failed' || slots.repaired?.status === 'failed'
            || sketch?.status === 'failed';
          if (failed) {
            setError(
              slots.repaired?.errorMessage
              || slots.original?.errorMessage
              || sketch?.errorMessage
              || '直觉草图生成失败',
            );
          } else {
            setNotice(null);
          }
        }
      } catch {
        /* keep current */
      }
    };
    void tick();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [
    ipttqId,
    record?.skill?.intuitionSketch?.status,
    record?.skill?.intuitionSketch?.slots?.original?.status,
    record?.skill?.intuitionSketch?.slots?.repaired?.status,
  ]);

  const bankIdsKey = record?.bankParticleIds.join(',') || '';

  useEffect(() => {
    if (!record || record.bankParticleIds.length === 0) {
      setBankParticles({});
      setBankLoading(false);
      return;
    }
    let cancelled = false;
    setBankLoading(true);
    void fetchWorkbenchParticles(record.bankParticleIds).then((next) => {
      if (cancelled) return;
      setBankParticles(next);
      setBankLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [record?.id, bankIdsKey]);

  useEffect(() => {
    if (!focusParticleId || bankLoading || !record?.bankParticleIds.includes(focusParticleId)) {
      return;
    }
    const el = document.querySelector<HTMLElement>(
      `[data-bank-particle="${CSS.escape(focusParticleId)}"]`,
    );
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(() => {
      setSearchParams((prev) => {
        if (!prev.has('focusParticle')) return prev;
        const next = new URLSearchParams(prev);
        next.delete('focusParticle');
        return next;
      }, { replace: true });
    }, 2600);
    return () => window.clearTimeout(timer);
  }, [focusParticleId, bankLoading, bankIdsKey, record, setSearchParams]);

  const handleRefreshSkill = async () => {
    if (!record || bankRunState !== 'idle' || startingBankRef.current) return;
    if (noReferenceParticles) {
      setError('品题尚无知识颗粒，请先加入参考题后再更新品题浓缩结果');
      setNotice(null);
      return;
    }
    setRefreshingScope('skill');
    setError(null);
    setNotice(null);
    try {
      const data = await ipttqService.refresh(record.id, { scope: 'skill' });
      setRecord(data.record);
      applyBankGenerationState(data.record);
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新品题浓缩结果失败');
    } finally {
      setRefreshingScope(null);
    }
  };

  const handleRefreshIsk = async () => {
    if (!record || refreshingIsk || uploadingIsk || repairingIsk || bankRunState !== 'idle') return;
    if (!iskAiGenerationAllowed) {
      setError(ISK_AI_GENERATION_RESTRICTED_NOTICE);
      setNotice(null);
      return;
    }
    const selected = iskModels.find((item) => item.id === (iskModel || iskDefaultModel));
    if (selected?.unavailable) {
      setError('该生图模型暂时不可用，请改选其他模型');
      return;
    }
    setRefreshingIsk(true);
    setIskPreviewSlot('original');
    setError(null);
    setNotice(null);
    try {
      const data = await ipttqService.refreshIsk(record.id, {
        model: iskModel || iskDefaultModel,
        provider: (iskProvider as 'dashscope' | 'banana' | 'image2' | 'wavespeed') || 'image2',
      });
      setRecord(data.record);
      applyBankGenerationState(data.record);
      if (iskGenerationPending(data.record.skill?.intuitionSketch)) {
        setNotice(data.alreadyRunning ? '直觉草图正在生成中' : '已开始生成直觉草图');
      } else {
        setRefreshingIsk(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新直觉草图失败');
      setRefreshingIsk(false);
    }
  };

  const handleUploadIskFile = async (file: File | null | undefined) => {
    // 手绘上传与 AI 生图/修图互不阻塞；仅避免并发上传与题库生成冲突
    if (!record || !file || uploadingIsk || bankRunState !== 'idle' || archiving) return;
    if (isIpttqSessionOriginalsRef(record) || !ipttqHasSkillBody(record.skill)) {
      setError('请先更新品题浓缩结果，再上传手绘直觉草图');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件上传');
      return;
    }
    setUploadingIsk(true);
    setIskPreviewSlot('handDrawn');
    setError(null);
    setNotice(null);
    try {
      const data = await ipttqService.uploadIsk(record.id, file);
      setRecord(data.record);
      applyBankGenerationState(data.record);
      setNotice('已上传手绘直觉草图');
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传手绘草图失败');
    } finally {
      setUploadingIsk(false);
      if (iskUploadInputRef.current) iskUploadInputRef.current.value = '';
    }
  };

  const handleRepairIsk = async () => {
    if (!record || refreshingIsk || uploadingIsk || repairingIsk || bankRunState !== 'idle') return;
    if (!iskAiGenerationAllowed) {
      setError(ISK_AI_GENERATION_RESTRICTED_NOTICE);
      setNotice(null);
      return;
    }
    setRepairingIsk(true);
    setRefreshingIsk(true);
    setIskPreviewSlot('repaired');
    setError(null);
    setNotice(null);
    try {
      const data = await ipttqService.repairIsk(record.id, {
        model: iskModel || iskDefaultModel,
        provider: (iskProvider as 'dashscope' | 'banana' | 'image2' | 'wavespeed') || 'image2',
      });
      setRecord(data.record);
      applyBankGenerationState(data.record);
      if (iskGenerationPending(data.record.skill?.intuitionSketch)) {
        setNotice(data.alreadyRunning ? '修图进行中' : '已开始手绘修图');
      } else {
        setRefreshingIsk(false);
        setRepairingIsk(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '手绘修图失败');
      setRefreshingIsk(false);
      setRepairingIsk(false);
    }
  };

  const handleSelectIskSlot = async (slot: IpttqIskSlotKind) => {
    if (!record || refreshingIsk || uploadingIsk || repairingIsk || bankRunState !== 'idle') return;
    setIskPreviewSlot(slot);
    setError(null);
    try {
      const data = await ipttqService.selectIskSlot(record.id, slot);
      setRecord(data.record);
      applyBankGenerationState(data.record);
    } catch (err) {
      setError(err instanceof Error ? err.message : '选用直觉草图失败');
    }
  };

  const handleGenerateBank = async () => {
    if (!record || bankRunState !== 'idle' || startingBankRef.current) return;
    const remaining = Math.max(0, 5 - record.bankParticleIds.length);
    if (remaining <= 0) return;
    const total = Math.min(Math.max(1, clampedCount), remaining);
    startingBankRef.current = true;
    setError(null);
    setNotice(null);
    try {
      const data = await ipttqService.generateBank(record.id, { count: total });
      setRecord(data.record);
      applyBankGenerationState(data.record);
      setNotice(data.alreadyRunning ? '诱导题库正在生成中' : '已开始生成诱导题库（可离开本页）');
    } catch (err) {
      setError(err instanceof Error ? err.message : '生成题库失败');
      setBankRunState('idle');
    } finally {
      startingBankRef.current = false;
    }
  };

  const handleArchive = async () => {
    if (!record || archiving || bankRunState !== 'idle') return;
    const title = stripIpttqDomainTerms(record.skill.mainTitle) || '未命名模式';
    if (!window.confirm(`确定删除「${title}」？删除后不可恢复。`)) return;
    setArchiving(true);
    setMenuOpen(false);
    setError(null);
    try {
      await ipttqService.archive(record.id);
      navigate(sessionPath, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除品题浓缩结果失败');
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-primary" />
      </div>
    );
  }

  if (!record) {
    return (
      <div className="space-y-4">
        <Link to={sessionPath} className="text-sm text-primary">← 返回品题详情</Link>
        <p className="text-danger">{error || '未找到品题浓缩结果'}</p>
      </div>
    );
  }

  const warn = ipttqNeedsBankWarning(record);
  const iskWarn = ipttqNeedsIskWarning(record);
  const isPlain = isIpttqSessionOriginalsRef(record);
  const skill = record.skill;
  const bankBusy = bankRunState !== 'idle';
  const busy = Boolean(refreshingScope) || bankBusy || refreshingIsk || uploadingIsk || repairingIsk;
  const skillEmpty = skillLooksEmpty(record);
  const bankFull = record.bankParticleIds.length >= 5;
  const refreshBlocked = busy || archiving || noReferenceParticles;
  const thinkingLineage = skill.thinkingLineage?.trim() || '';
  const displayMainTitle = stripIpttqDomainTerms(skill.mainTitle) || '未命名模式';
  const displaySubtitle = stripIpttqDomainTerms(skill.subtitle || '') || null;
  const displayNotes = resolveLearnerFacingNotes(skill.notes);
  const isk = skill.intuitionSketch;
  const iskSlots = getIskSlots(isk);
  const selectedIskSlot = resolveIskSelectedSlot(isk);
  const activePreviewSlot = iskPreviewSlot
    && (iskSlotHasImage(iskSlots[iskPreviewSlot]) || iskSlots[iskPreviewSlot]?.status === 'pending')
    ? iskPreviewSlot
    : (selectedIskSlot || 'original');
  const previewSlotImage = getIskSlotImage(isk, activePreviewSlot);
  const iskUrl = String(previewSlotImage?.imageUrl || '').trim();
  const iskOssId = String(previewSlotImage?.imageOssId || '').trim();
  const previewCost = previewSlotImage?.cost;
  const hasHandDrawn = iskSlotHasImage(iskSlots.handDrawn);

  const bankStatusLine = isPlain
    ? `${formatIpttqBankMetaLine(record, { warn })} · ${record.lifecycle}`
    : skillEmpty
      ? `题库 ${record.bankParticleIds.length}/5 · 浓缩结果为空`
      : [
          warn
            ? `题库 ${record.bankParticleIds.length}/5 · 红灯：尚无诱导题`
            : `题库 ${record.bankParticleIds.length}/5 · 已有诱导题 · 消费 ${record.bankConsumeCount} · ${record.lifecycle}`,
          iskWarn ? ipttqIskWarningLabel(record) : null,
        ].filter(Boolean).join(' · ');

  return (
    <div className="mx-auto max-w-2xl">
      <div className="sticky top-0 z-20 space-y-1.5 border-b border-border bg-canvas/95 pb-2 backdrop-blur">
        <div className="flex items-center gap-2">
          <Link to={sessionPath} className="shrink-0 text-sm text-primary">← 返回</Link>
          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            {isPlain ? null : (
              <>
                <button
                  type="button"
                  disabled={refreshBlocked}
                  className={
                    'inline-flex h-8 items-center gap-1 rounded-md border bg-surface px-2 text-xs font-medium hover:bg-black/[0.03] disabled:opacity-50 '
                    + (scopeDrifted
                      ? 'border-amber-400/70 text-amber-800'
                      : 'border-border text-ink')
                  }
                  onClick={() => void handleRefreshSkill()}
                >
                  {refreshingScope === 'skill' ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Sparkles size={12} />
                  )}
                  {scopeDrifted ? '更新品题浓缩结果（建议）' : '更新品题浓缩结果'}
                </button>
                <label className="sr-only" htmlFor="ipttq-page-bank-count">生成数量</label>
                <input
                  id="ipttq-page-bank-count"
                  type="number"
                  min={1}
                  max={maxCount}
                  disabled={busy || bankFull || skillEmpty || noReferenceParticles}
                  value={clampedCount}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (!Number.isFinite(next)) return;
                    setBankCount(Math.min(maxCount, Math.max(1, Math.floor(next))));
                  }}
                  className="h-8 w-11 rounded-md border border-border bg-surface px-1 text-center text-xs text-ink disabled:opacity-50"
                />
                <button
                  type="button"
                  disabled={busy || bankFull || skillEmpty || noReferenceParticles}
                  className="inline-flex h-8 items-center gap-1 rounded-md bg-primary px-2.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                  onClick={() => void handleGenerateBank()}
                >
                  {bankBusy ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Layers size={12} />
                  )}
                  {bankFull ? '题库已满' : '生成'}
                </button>
              </>
            )}
            <div className="relative" ref={menuRootRef}>
              <button
                type="button"
                disabled={archiving || bankBusy}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-surface text-ink hover:bg-black/[0.03] disabled:opacity-50"
                aria-label="更多"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((value) => !value)}
              >
                {archiving ? <Loader2 size={14} className="animate-spin" /> : <MoreVertical size={14} />}
              </button>
              {menuOpen ? (
                <div className="absolute right-0 z-40 mt-1 min-w-[140px] overflow-hidden rounded-md border border-border bg-surface shadow-lg">
                  <button
                    type="button"
                    disabled={archiving}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-danger hover:bg-danger/5 disabled:opacity-50"
                    onClick={() => {
                      setMenuOpen(false);
                      void handleArchive();
                    }}
                  >
                    <Trash2 size={14} />
                    删除
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <h1 className="text-sm font-semibold leading-snug text-ink">
            <span className="font-normal text-muted">品题浓缩结果 · </span>
            <span className="ai-chat-assistant-body">
              {displayMainTitle}
              {!displaySubtitle ? <AiGeneratedInlineMark baseFontSize={14} /> : null}
            </span>
            {isPlain ? (
              <span className="ml-1 text-[10px] font-normal text-primary">平凡</span>
            ) : null}
            {record.isDefault ? (
              <span className="ml-1 text-[10px] font-normal text-muted">默认</span>
            ) : null}
          </h1>
          {isPlain ? (
            <p className="mt-0.5 text-[11px] leading-snug text-muted">平凡品题 · 引用原始知识颗粒</p>
          ) : displaySubtitle ? (
            <p
              className="ai-chat-assistant-body mt-0.5 text-[11px] leading-snug text-muted"
              title={displaySubtitle}
            >
              {displaySubtitle}
              <AiGeneratedInlineMark baseFontSize={11} />
            </p>
          ) : null}
        </div>

        <p
          className={
            'flex min-w-0 items-center gap-1 truncate text-[11px] '
            + (warn || skillEmpty
              ? 'text-danger'
              : iskWarn
                ? 'text-amber-700'
                : 'text-muted')
          }
          title={
            skillEmpty
              ? '请先更新品题浓缩结果，再生成诱导题库'
              : warn
                ? '可用「生成」逐题补诱导题（最多 5）'
                : iskWarn
                  ? '可用「更新直觉草图」单独生成'
                  : bankStatusLine
          }
        >
          {warn || skillEmpty || iskWarn ? <AlertCircle size={12} className="shrink-0" /> : null}
          <span className="truncate">{bankStatusLine}</span>
        </p>

        {refreshingScope === 'skill' ? (
          <p className="flex items-center gap-1.5 text-[11px] text-muted">
            <Loader2 size={11} className="animate-spin" />
            正在更新品题浓缩结果…
          </p>
        ) : null}

        {noReferenceParticles ? (
          <p className="text-[11px] text-amber-700">
            本品题尚无知识颗粒，请先加入参考题后再更新品题浓缩结果（避免空跑消耗 tokens）。
          </p>
        ) : null}

        {scopeDrifted && !noReferenceParticles ? (
          <p className="flex items-start gap-1 text-[11px] text-amber-700">
            <AlertCircle size={12} className="mt-0.5 shrink-0" />
            <span>{IPTTQ_SCOPE_DRIFT_TIP}</span>
          </p>
        ) : null}

        {bankBusy ? (
          <p className="flex items-center gap-1.5 text-[11px] text-muted">
            <Loader2 size={11} className="animate-spin" />
            <span>正在生成诱导题库…（可离开本页，稍后回来查看）</span>
          </p>
        ) : null}

        {notice ? (
          <p className="truncate text-[11px] text-primary" title={notice}>{notice}</p>
        ) : null}

        {error ? (
          <p className="truncate text-[11px] text-danger" title={error}>{error}</p>
        ) : null}
      </div>

      <div className="space-y-5 pb-10 pt-5">
        <p className="text-[11px] text-muted">
          生成模型：
          {skill.generatorModel
            ? (
              <>
                <span className="font-medium text-ink/80">{skill.generatorModel}</span>
                {skill.generatorProvider ? (
                  <span className="text-muted">（{skill.generatorProvider}）</span>
                ) : null}
              </>
            )
            : '未记录（更新品题浓缩结果后可见）'}
        </p>

        {!isPlain ? (
          <section>
            <h2 className="mb-1.5 text-sm font-semibold text-ink">直觉草图</h2>
            <p className="mb-2 text-[11px] text-muted">
              上方预览、下方三槽：原始AI生成直觉草图 / 手绘直觉草图 / 手绘直觉草图AI修图。勾选后作为当前直觉草图。
            </p>
            <input
              ref={iskUploadInputRef}
              id="ipttq-isk-hand-drawn-upload"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(event) => {
                void handleUploadIskFile(event.target.files?.[0]);
              }}
            />
            <div className="relative min-w-0">
                {iskUrl || iskOssId ? (
                  <div className="relative w-full">
                    <ProxyImage
                      rawUrl={iskUrl || null}
                      ossId={iskOssId || undefined}
                      alt="直觉草图预览"
                      className="w-full rounded-md border border-border bg-white"
                      imgClassName="max-h-[28rem]"
                      fallbackText="直觉草图加载失败"
                    />
                    <AiGeneratedImageCornerMark />
                    {ipttqIskIsStale(record) ? (
                      <p className="mt-1.5 text-[11px] text-amber-700">
                        浓缩结果已更新，可更新直觉草图（每人每日有生图限额）
                      </p>
                    ) : null}
                    {Number.isFinite(Number(previewCost)) ? (
                      <div className="absolute right-1.5 top-1.5 flex items-center gap-1 rounded-md border border-border/80 bg-canvas/90 px-1 py-0.5 shadow-sm backdrop-blur">
                        <button
                          type="button"
                          title={iskCostVisible ? '隐藏费用' : '显示费用'}
                          aria-label={iskCostVisible ? '隐藏草图费用' : '显示草图费用'}
                          aria-pressed={iskCostVisible}
                          className="inline-flex h-5 w-5 items-center justify-center rounded text-muted hover:bg-black/[0.05] hover:text-ink"
                          onClick={() => setIskCostVisible((value) => !value)}
                        >
                          <Info size={12} />
                        </button>
                        {iskCostVisible ? (
                          <button
                            type="button"
                            title="查看本品题累计 tokens"
                            aria-label={`草图费用 ${formatIskCostDisplay(previewCost)}，点击查看本品题累计 tokens`}
                            disabled={sessionTokenHintLoading}
                            className="pr-0.5 text-[10px] font-medium tabular-nums text-ink/80 hover:text-ink disabled:opacity-50"
                            onClick={() => {
                              if (!ttqaId || sessionTokenHintLoading) return;
                              setSessionTokenHintLoading(true);
                              void ttqService.getSession(ttqaId)
                                .then((session) => {
                                  const total = Number(session.tokenUsageSummary?.totalTokens) || 0;
                                  setIskCostRecordHint(
                                    `本品题累计 ${total.toLocaleString('zh-CN')} tokens`,
                                  );
                                })
                                .catch(() => {
                                  setIskCostRecordHint('暂无法读取本品题 token 消耗');
                                })
                                .finally(() => {
                                  setSessionTokenHintLoading(false);
                                  window.setTimeout(() => setIskCostRecordHint(null), 3200);
                                });
                            }}
                          >
                            {formatIskCostDisplay(previewCost)}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex min-h-[12rem] items-center justify-center rounded-md border border-dashed border-border bg-canvas/40 px-3 text-center text-sm text-muted">
                    {ipttqIskPlaceholderHint(record)
                      || (previewSlotImage?.status === 'pending'
                        ? '正在生成…'
                        : previewSlotImage?.status === 'failed'
                          ? (previewSlotImage.errorMessage || '生成失败')
                          : '点下方槽预览或生成')}
                  </div>
                )}
                {iskCostRecordHint ? (
                  <p className="mt-1 text-[11px] text-muted">{iskCostRecordHint}</p>
                ) : null}
                {previewSlotImage?.status === 'failed' && (iskUrl || iskOssId) ? (
                  <div className="mt-1.5">
                    <IskErrorNotice
                      errorMessage={previewSlotImage.errorMessage}
                      hasStaleImage
                      emptyPrefix="上次生成未成功"
                    />
                  </div>
                ) : null}
            </div>

            <ul className="mt-3 flex flex-col gap-2.5">
                {ISK_SLOT_ROWS.map(({ kind, label }) => {
                  const slot = iskSlots[kind];
                  const hasImage = iskSlotHasImage(slot);
                  const pending = slot?.status === 'pending';
                  const isSelected = selectedIskSlot === kind;
                  const isPreview = activePreviewSlot === kind;
                  const canActSkill = !isPlain && ipttqHasSkillBody(skill) && !archiving && bankRunState === 'idle';
                  const aiRestricted = (kind === 'original' || kind === 'repaired') && !iskAiGenerationAllowed;
                  const actionDisabled = kind === 'handDrawn'
                    ? !canActSkill || uploadingIsk
                    : aiRestricted
                      ? false
                      : !canActSkill || busy || (kind === 'repaired' && !hasHandDrawn);
                  let actionLabel = '';
                  if (kind === 'original') {
                    actionLabel = hasImage ? '更新原始AI草图' : '生成原始AI草图';
                  } else if (kind === 'handDrawn') {
                    actionLabel = hasImage ? '更新手绘直觉草图' : '上传手绘直觉草图';
                  } else {
                    actionLabel = '手绘直觉草图AI修图';
                  }
                  const thumbUrl = String(slot?.imageUrl || '').trim();
                  const thumbOss = String(slot?.imageOssId || '').trim();
                  const slotBusy = (kind === 'original' && refreshingIsk && !repairingIsk)
                    || (kind === 'repaired' && repairingIsk)
                    || (kind === 'handDrawn' && uploadingIsk);

                  return (
                    <li key={kind}>
                      <div
                        className={
                          'flex overflow-hidden rounded-md border bg-white '
                          + (isPreview ? 'border-primary ring-1 ring-primary/30' : 'border-border')
                        }
                      >
                        <button
                          type="button"
                          className="h-24 w-24 shrink-0 bg-canvas/40"
                          onClick={() => setIskPreviewSlot(kind)}
                          aria-label={`预览${label}`}
                        >
                          {thumbUrl || thumbOss ? (
                            <ProxyImage
                              rawUrl={thumbUrl || null}
                              ossId={thumbOss || undefined}
                              alt={label}
                              className="h-full w-full"
                              imgClassName="h-full w-full object-cover"
                              fallbackText={label}
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center px-1 text-center text-[11px] text-muted">
                              {pending ? '生成中…' : '暂无图'}
                            </div>
                          )}
                        </button>
                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 px-3 py-2.5">
                          <p className="text-sm font-semibold leading-snug text-ink">{label}</p>
                          {kind === 'handDrawn' ? (
                            <label
                              htmlFor="ipttq-isk-hand-drawn-upload"
                              className={
                                'inline-flex min-h-10 cursor-pointer items-center justify-center rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-white '
                                + (actionDisabled || slotBusy ? 'pointer-events-none opacity-45' : 'hover:opacity-90')
                              }
                              onClick={(event) => {
                                if (actionDisabled || slotBusy) {
                                  event.preventDefault();
                                  return;
                                }
                                if (!canActSkill) {
                                  event.preventDefault();
                                  setError('请先更新品题浓缩结果，再上传手绘直觉草图');
                                }
                              }}
                            >
                              {slotBusy ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <Loader2 size={14} className="animate-spin" />
                                  处理中…
                                </span>
                              ) : actionLabel}
                            </label>
                          ) : (
                            <button
                              type="button"
                              disabled={!aiRestricted && (actionDisabled || slotBusy)}
                              className={
                                'inline-flex min-h-10 items-center justify-center rounded-md px-3 py-2 text-sm font-semibold '
                                + (aiRestricted
                                  ? 'cursor-pointer bg-border/80 text-muted opacity-70'
                                  : 'bg-primary text-white hover:opacity-90 disabled:opacity-45')
                              }
                              onClick={() => {
                                if (aiRestricted) {
                                  setError(ISK_AI_GENERATION_RESTRICTED_NOTICE);
                                  setNotice(null);
                                  return;
                                }
                                if (kind === 'original') void handleRefreshIsk();
                                else void handleRepairIsk();
                              }}
                            >
                              {slotBusy && !aiRestricted ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <Loader2 size={14} className="animate-spin" />
                                  处理中…
                                </span>
                              ) : actionLabel}
                            </button>
                          )}
                          <label
                            className={
                              'inline-flex min-h-7 items-center gap-2 text-sm text-ink '
                              + (hasImage ? 'cursor-pointer' : 'opacity-40')
                            }
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-primary"
                              disabled={!hasImage || busy}
                              checked={isSelected}
                              onChange={() => {
                                if (!hasImage || isSelected) return;
                                void handleSelectIskSlot(kind);
                              }}
                            />
                            选为当前直觉草图
                          </label>
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
            <p className="mt-2 text-[10px] text-muted">
              {iskAiGenerationAllowed
                ? '原始AI生成与手绘AI修图共用每日额度 1 次；手绘上传不占额度'
                : 'AI 生图暂受限；手绘上传仍可用'}
            </p>
          </section>
        ) : null}

        <section>
          <h2 className="mb-1.5 text-sm font-semibold text-ink">思考脉络</h2>
          {thinkingLineage ? (
            <div className="ai-chat-assistant-body">
              <DocumentMathTextBlock
                text={thinkingLineage}
                className="text-sm leading-relaxed text-ink/90"
              />
              <AiGeneratedInlineMark baseFontSize={14} />
            </div>
          ) : (
            <p className="text-sm text-muted">（尚未写入）</p>
          )}
        </section>

        <section>
          <h2 className="mb-1.5 text-sm font-semibold text-ink">隐性能力</h2>
          <p className="mb-2 text-[11px] leading-relaxed text-muted">
            保留浓缩原文，并尽量绑定到现有 MindSet 词表标签。
          </p>
          <ImplicitCapabilityList
            capabilities={skill.implicitCapabilities}
            bindings={skill.implicitCapabilityBindings}
          />
        </section>

        {skill.answerMustShow && skill.answerMustShow.length > 0 ? (
          <section>
            <h2 className="mb-1.5 text-sm font-semibold text-ink">答案须体现</h2>
            <ul className="list-disc space-y-1.5 pl-4 text-sm text-ink/90">
              {skill.answerMustShow.map((item) => (
                <li key={item}>
                  <div className="ai-chat-assistant-body">
                    <DocumentMathTextBlock text={item} className="text-sm" />
                    <AiGeneratedInlineMark baseFontSize={14} />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {skill.forbiddenExplicitLabels && skill.forbiddenExplicitLabels.length > 0 ? (
          <section>
            <h2 className="mb-1.5 text-sm font-semibold text-ink">不宜作主定义的考点名</h2>
            <ChipList items={skill.forbiddenExplicitLabels} empty="" />
          </section>
        ) : null}

        {displayNotes ? (
          <section>
            <h2 className="mb-1.5 text-sm font-semibold text-ink">备注</h2>
            <div className="ai-chat-assistant-body">
              <DocumentMathTextBlock
                text={displayNotes}
                className="text-sm leading-relaxed text-muted"
              />
              <AiGeneratedInlineMark baseFontSize={14} />
            </div>
          </section>
        ) : null}

        {!warn && record.bankParticleIds.length > 0 ? (
          <section>
            <h2 className="mb-1.5 text-sm font-semibold text-ink">诱导题库</h2>
            {bankLoading ? (
              <div className="flex items-center gap-2 py-2 text-xs text-muted">
                <Loader2 size={12} className="animate-spin" />
                加载题库卡片…
              </div>
            ) : (
              <ul className="space-y-2">
                {record.bankParticleIds.map((id, index) => {
                  const particle = bankParticles[id];
                  const title = particle
                    ? getParticleTitle(particle) || `诱导题 ${index + 1}`
                    : `诱导题 ${index + 1}`;
                  const preview = particle
                    ? getParticlePreviewText(particle, 96)
                    : id.slice(0, 8);
                  const returnTo = `${ipttqPath}?focusParticle=${encodeURIComponent(id)}`;
                  const href = buildKnowledgeParticleDetailPath(id, returnTo);
                  const focused = focusParticleId === id;

                  return (
                    <li key={id}>
                      <Link
                        to={href}
                        data-bank-particle={id}
                        className={
                          'flex w-full items-start gap-2 rounded-lg border px-3 py-2.5 text-left transition '
                          + (focused
                            ? 'border-primary bg-primary/10 ring-2 ring-primary/40'
                            : 'border-border bg-canvas/30 hover:border-primary/40 hover:bg-canvas/60')
                        }
                      >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-primary/10 text-[10px] font-semibold text-primary">
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-medium text-ink">
                            <DocumentMathTextBlock text={title} inline className="text-xs" />
                          </span>
                          <span className="mt-0.5 block line-clamp-2 text-[11px] leading-relaxed text-muted">
                            <DocumentMathTextBlock text={preview} inline className="text-[11px]" />
                          </span>
                        </span>
                        <ChevronRight size={14} className="mt-1 shrink-0 text-muted" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
